import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormLayoutComposer, IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import { useFormSlot } from 'components/common/form/layout/control/FormComponentSlot';
import { InfoMessageType, ReIndexRequiredLine } from 'components/common/form/layout/helper/ReIndexRequiredLine';
import { ActAfterSaveSuccess } from 'components/common/form/act';
import useWatchFormFieldsChanges from 'components/common/form/hooks/useWatchFormFieldsChanges';
import useWatchFormGridsChanges from 'components/common/form/hooks/useWatchFormGridsChanges';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import { APIResponse, YNValues } from 'tools/types';

type IndexationType = YNValues & 'Y-access';
export default function SearchForm(props: IFormLayoutProps) {
    const slotComponent = useFormSlot('dShowIndexRequired');

    const [wasReset, setWasReset] = useState(false);
    const { data: needIndexation } = useQuery<IndexationType, Error>({
        queryKey: ['ConciergeIndexAlert'],
        queryFn: async () => {
            const result = await instance.get<APIResponse<{ reindex_required_ind: IndexationType }>>(
                'data/admin/search/check-reindex-needed',
            );
            return (result.data.status == 'OK' ? result.data.data.reindex_required_ind : 'N') as IndexationType;
        },
        refetchOnMount: 'always',
        placeholderData: 'N' as IndexationType,
    });
    useEffect(() => {
        if (needIndexation != 'N') {
            props.hookForm.form.hookFormSetValue('fieldsChanged', 'Y');
        }
    }, [needIndexation]);

    const [showMessage, setShowMessage] = useState<InfoMessageType>('none');
    const [fields] = useState<Array<string>>(
        props.componentsMap.filter((c) => c.props.component.component != 'GridComponent').map((c) => c.componentName),
    );
    const [formWasSaved, setFormWasSaved] = useState(false);
    useEffect(() => {
        props.hookForm.form.formAct.overrideAct(
            'afterSaveSuccess',
            class extends ActAfterSaveSuccess {
                async exec(response: any, data: any): Promise<any> {
                    setFormWasSaved(true);
                    const flag = props.hookForm.form.hookFormGetValues('fieldsChanged');
                    if (flag) {
                        const updateButton = document.getElementById('update_indexes_button');
                        if (updateButton) {
                            updateButton.click();
                        }
                    }
                    return super.exec(response, data);
                }
            },
        );
    }, [props.hookForm.form]);

    const resetFields = useWatchFormFieldsChanges(props.hookForm.form, fields, 'fieldsChanged');
    const flag = props.hookForm.form.hookFormWatch('fieldsChanged');
    const { changedGrids, resetChangedGrids } = useWatchFormGridsChanges(props.hookForm.form, [
        'reference_grid',
        'dataset_grid',
        'alias_grid',
    ]);

    useEffect(() => {
        if (changedGrids.length) {
            props.hookForm.form.hookFormSetValue('fieldsChanged', 'Y');
        }
    }, [changedGrids]);

    useEffect(() => {
        if (needIndexation != 'N' && !wasReset) {
            setShowMessage(needIndexation == 'Y' ? 'optimized' : 'access');
        }
        if (!formWasSaved) {
            return;
        }

        if (flag == 'Y') {
            setShowMessage('optimized');
        }

        if (flag == '') {
            setWasReset(true);
            setShowMessage('none');
            resetFields();
            resetChangedGrids();
            setFormWasSaved(false);
        }
    }, [flag, formWasSaved, needIndexation, wasReset]);

    return (
        <>
            <FormLayoutComposer props={props} />
            {slotComponent &&
                createPortal(
                    <ReIndexRequiredLine
                        form={props.hookForm.form}
                        messageType={showMessage}
                        resetFields={['fieldsChanged']}
                    />,
                    slotComponent,
                )}
        </>
    );
}
